import ConsultationsView from '@/views/ConsultationsView.vue'
import type { RouteRecordRaw } from 'vue-router'
import {
  hasAddPatientsPermission,
  hasConnectPluginLicense,
  hasGodModeShiftAndNotHospitalAdmin,
  hasListPatientsPermission,
  hasReceiveConsultationsPermission,
  hasSendConsultationsPermission,
  hasUserHospitalItRole,
  hasUserTccItRole,
  isLegacyRoutingEnabledForHospital,
  restrictFromHospitalAdmin
} from './routerguards'
import { routeNames } from './routeNames'
import VitalSignsView from '@/views/VitalSignsView.vue'
import VitalDataSettingsView from '@/views/VitalDataSettingsView.vue'
import { defineAsyncComponent } from 'vue'
import { useUserStore } from '@/stores/user'
import { UserRightsEnum } from '@/generated'

export const routes: RouteRecordRaw[] = [
  {
    path: '/plugin-not-licensed',
    name: routeNames.PLUGIN_NOT_LICENSED,
    component: () => import('../views/PluginNotLicensedView.vue')
  },
  {
    path: '/',
    name: routeNames.CONSULTATIONS,
    component: ConsultationsView,
    props: (route) => ({ initialTab: route.query.tab }),
    beforeEnter: [hasConnectPluginLicense, restrictFromHospitalAdmin]
  },
  {
    path: '/vital-data',
    name: routeNames.VITAL_DATA,
    component: VitalSignsView
  },
  {
    path: '/vital-data-settings',
    name: routeNames.VITAL_DATA_SETTINGS,
    component: VitalDataSettingsView,
    beforeEnter: [hasListPatientsPermission, restrictFromHospitalAdmin]
  },
  {
    path: '/user-profile',
    name: routeNames.USER_PROFILE,
    component: () => import('../views/UserProfileView.vue'),
    beforeEnter: restrictFromHospitalAdmin
  },
  {
    path: '/logout-handover',
    name: routeNames.LOGOUT_HANDOVER,
    component: () => import('../views/LogoutHandoverView.vue'),
    beforeEnter: [hasSendConsultationsPermission, hasGodModeShiftAndNotHospitalAdmin]
  },
  {
    path: '/notification-center',
    name: routeNames.NOTIFICATION_CENTER,
    component: () => import('../views/NotificationCenterView.vue'),
    beforeEnter: hasGodModeShiftAndNotHospitalAdmin
  },

  {
    path: '/new-consultation',
    name: routeNames.NEW_CONSULTATION,
    component: () => import('../views/NewConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/new-consultation/ward-round',
    name: routeNames.CREATE_WARD_ROUND,
    props: {
      translationKey: 'Create ward round',
      consultationForm: defineAsyncComponent(
        () => import('../components/consultationrequest/CreateWardRoundForm.vue')
      )
    },
    component: () => import('../views/CreateScheduledConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/new-consultation/case-conference',
    name: routeNames.CREATE_CASE_CONFERENCE,
    props: {
      translationKey: 'Create case conference',
      consultationForm: defineAsyncComponent(
        () => import('../components/consultationrequest/CreateCaseConferenceForm.vue')
      )
    },
    component: () => import('../views/CreateScheduledConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/new-consultation/patient-presentation',
    name: routeNames.CREATE_PATIENT_PRESENTATION,
    props: {
      translationKey: 'Create patient presentation',
      consultationForm: defineAsyncComponent(
        () => import('../components/consultationrequest/CreatePatientPresentationForm.vue')
      )
    },
    component: () => import('../views/CreateScheduledConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/new-consultation/urgent',
    name: routeNames.CREATE_URGENT_CONSULTATION,
    component: () => import('../views/CreateUrgentConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/new-consultation/routine',
    name: routeNames.CREATE_ROUTINE_CONSULTATION,
    component: () => import('../views/CreateRoutineConsultationView.vue'),
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/add-patient',
    name: routeNames.ADD_PATIENT,
    component: () => import('../views/AddPatientView.vue'),
    beforeEnter: [hasListPatientsPermission, hasAddPatientsPermission, restrictFromHospitalAdmin]
  },
  {
    path: '/admin',
    name: routeNames.HOSPITAL_ADMIN,
    beforeEnter: hasUserHospitalItRole,
    children: [
      {
        path: '',
        redirect: () => {
          const userStore = useUserStore()
          if (userStore.user?.currentRole === UserRightsEnum.HOSPITAL_IT) {
            return { name: routeNames.ADMIN_USERS }
          }
          return { name: routeNames.ADMIN_HOSPITALS }
        },
        name: routeNames.HOSPITAL_ADMIN_REDIRECT
      },
      {
        path: 'routing',
        name: routeNames.ADMIN_ROUTING,
        component: () => import('@/hospitalAdminTool/views/AdminRoutingView.vue'),
        beforeEnter: [hasConnectPluginLicense, hasUserTccItRole]
      },
      {
        path: 'hospitals',
        name: routeNames.ADMIN_HOSPITALS,
        component: () => import('@/hospitalAdminTool/views/AdminHospitalsView.vue')
      },
      {
        path: 'users/:hospitalId?',
        name: routeNames.ADMIN_USERS,
        component: () => import('@/hospitalAdminTool/views/AdminUsersView.vue'),
        props: true
      },
      {
        path: 'patients/:hospitalId?',
        name: routeNames.ADMIN_PATIENTS,
        component: () => import('@/hospitalAdminTool/views/AdminPatientsView.vue'),
        props: true
      },
      {
        path: 'settings/:hospitalId?',
        name: routeNames.ADMIN_SETTINGS,
        component: () => import('@/hospitalAdminTool/views/AdminSettingsView.vue'),
        props: true,
        beforeEnter: hasUserTccItRole,
        children: [
          {
            path: routeNames.ADMIN_SETTINGS_VITAL_DATA,
            name: routeNames.ADMIN_SETTINGS_VITAL_DATA,
            component: () => import('@/hospitalAdminTool/settings/VitalDataSettings.vue'),
            props: true
          },
          {
            path: routeNames.ADMIN_SETTINGS_LINKED_HOSPITALS,
            name: routeNames.ADMIN_SETTINGS_LINKED_HOSPITALS,
            component: () => import('@/hospitalAdminTool/settings/LinkedHospitals.vue'),
            props: true
          },
          {
            path: routeNames.ADMIN_SETTINGS_ROUTING_LOG,
            name: routeNames.ADMIN_SETTINGS_ROUTING_LOG,
            children: [
              {
                path: '',
                name: routeNames.ADMIN_SETTINGS_ROUTING_LOG_TABLE,
                component: () => import('@/hospitalAdminTool/settings/RoutingLog.vue'),
                beforeEnter: [hasConnectPluginLicense]
              },
              {
                path: ':id',
                name: routeNames.ADMIN_SETTINGS_ROUTING_LOG_DETAILS,
                component: () =>
                  import('@/hospitalAdminTool/settings/routingLog/RoutingLogDetails.vue'),
                props: true,
                beforeEnter: [hasConnectPluginLicense]
              }
            ]
          },
          {
            path: routeNames.ADMIN_SETTINGS_ROUTING,
            name: routeNames.ADMIN_SETTINGS_ROUTING,
            component: () => import('@/hospitalAdminTool/settings/RoutingSettings.vue'),
            props: true,
            beforeEnter: [hasConnectPluginLicense, isLegacyRoutingEnabledForHospital]
          },
          {
            path: routeNames.ADMIN_SETTINGS_ACCOUNT,
            name: routeNames.ADMIN_SETTINGS_ACCOUNT,
            component: () => import('@/hospitalAdminTool/settings/AccountSettings.vue'),
            props: true
          }
        ]
      },
      {
        path: 'routing-change-log/:hospitalId?',
        name: routeNames.ADMIN_ROUTING_CHANGE_LOG,
        component: () => import('@/hospitalAdminTool/views/AdminRoutingChangeLogView.vue'),
        props: true,
        beforeEnter: [hasConnectPluginLicense, hasUserTccItRole]
      },
      {
        path: 'consultations-tracking/:hospitalId?',
        name: routeNames.ADMIN_CONSULTATIONS_TRACKING,
        component: () => import('@/hospitalAdminTool/views/AdminConsultationsTrackingView.vue'),
        props: true,
        beforeEnter: [hasConnectPluginLicense, hasUserTccItRole]
      }
    ]
  },
  {
    path: '/scheduled-call/:consultationId',
    name: routeNames.SCHEDULED_CALL,
    component: () => import('../views/ScheduledCallView.vue'),
    props: (route) => ({
      consultationId: route.params.consultationId,
      shareCode: route.query.shareCode
    }),
    beforeEnter: [
      hasConnectPluginLicense,
      hasReceiveConsultationsPermission,
      restrictFromHospitalAdmin
    ]
  },
  {
    path: '/patient-details/:caseId',
    name: routeNames.PATIENT_DETAILS,
    component: () => import('../views/PatientDetailView.vue'),
    props: (route) => ({
      caseId: route.params.caseId,
      shareCode: route.query.shareCode
    }),
    beforeEnter: [hasListPatientsPermission, restrictFromHospitalAdmin]
  },
  {
    path: '/scheduled-consultation-patient-details/:caseId',
    name: routeNames.SCHEDULED_CONSULTATION_PATIENT_DETAILS,
    component: () => import('../views/PatientDetailView.vue'),
    props: true,
    beforeEnter: [hasConnectPluginLicense, hasListPatientsPermission, restrictFromHospitalAdmin]
  },
  {
    path: '/consultation-details/:consultationId',
    name: routeNames.CONSULTATION_DETAILS,
    component: () => import('../views/ConsultationDetailView.vue'),
    props: true,
    beforeEnter: [
      hasConnectPluginLicense,
      hasReceiveConsultationsPermission,
      restrictFromHospitalAdmin
    ]
  },
  {
    path: '/edit-patient/:caseId',
    name: routeNames.EDIT_PATIENT,
    props: true,
    component: () => import('../views/EditPatientView.vue'),
    beforeEnter: [hasListPatientsPermission, hasAddPatientsPermission, restrictFromHospitalAdmin]
  },
  {
    path: '/direct-consultation-report/:consultationRequestId',
    name: routeNames.WRITE_DIRECT_CONSULTATION_REPORT,
    component: () => import('../views/ConsultationReportView.vue'),
    props: true,
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/scheduled-consultation-report/:scheduledConsultationId/cases/:caseId',
    name: routeNames.WRITE_SCHEDULED_CONSULTATION_REPORT,
    component: () => import('../views/ScheduledConsultationReportView.vue'),
    props: true,
    beforeEnter: [
      hasConnectPluginLicense,
      hasSendConsultationsPermission,
      hasGodModeShiftAndNotHospitalAdmin
    ]
  },
  {
    path: '/:consultationShareCode',
    name: routeNames.JOIN_CONSULTATION,
    component: () => import('../views/JoinConsultationCallView.vue'),
    props: true,
    beforeEnter: [
      hasConnectPluginLicense,
      hasReceiveConsultationsPermission,
      restrictFromHospitalAdmin
    ]
  }
]
